import { useState, useRef, useCallback, useEffect } from 'react';
import MojitoCore from 'mojito/core';
import Button from 'presentation/components/button/index.js';
import Image from 'presentation/components/image/index.js';
import ScrollPane from 'presentation/components/scroll-pane/index.js';
import FlexPane from 'presentation/components/flex-pane/index.js';
import AbsolutePane from 'presentation/components/absolute-pane/index.js';
import { isEmpty } from 'mojito/utils';
import { useEffectOnUpdate } from 'presentation/hooks/index.js';

const classUtils = MojitoCore.Base.classUtils;

const SCROLL_DIRECTION = {
    LEFT: -1,
    RIGHT: 1,
};

const SCROLL_BUTTON_POSITION = {
    LEFT: 'left',
    RIGHT: 'right',
};

const SCROLL_BEHAVIOR = {
    START: 'start',
    CENTER: 'center',
    END: 'end',
};

const ButtonScrollPane = ({
    refs,
    children,
    selectedItemKey,
    showScrollButtons,
    mojitoTools,
    elementRef,
    class: componentClass,
}) => {
    const { config, style } = mojitoTools;
    const [targetScrollPosition, setTargetScrollPosition] = useState(0);
    const [scrollPaneState, setScrollPaneState] = useState({
        scrollPosition: 0,
        maxScrollPosition: 0,
        scrollPaneWidth: undefined,
    });

    const [isHovered, setIsHovered] = useState(false);
    const [isAnimationEnabled, setIsAnimationEnabled] = useState(config.enableInitAnimation);
    const { scrollPosition, maxScrollPosition, scrollPaneWidth } = scrollPaneState;

    const leftScrollButtonRef = useRef();
    const rightScrollButtonRef = useRef();
    const scrollRef = useRef();

    const stringifiedRefsKeys = Object.keys(refs).join('-');

    useEffect(() => {
        return () => {
            // We had setTargetScrollPosition scrolling sticking issue because
            // of setTargetScrollPosition updates in onScrollPositionChanged method,
            // causing race conditions issues, when switching market group buttons fast.
            // So after we got rid of it we faced another issue that is fixed here,
            // where the scroll pane was scrolled by the user without changing selectedItemKey
            // after user navigates between the similar pages (i.e. event pages).
            if (scrollRef.current) {
                scrollRef.current?.scrollTo({
                    top: 0,
                    left: 0,
                });
            }
        };
    }, [stringifiedRefsKeys]);

    useEffectOnUpdate(() => {
        scrollToSelectedItem(maxScrollPosition);
        setIsAnimationEnabled(true);
    }, [stringifiedRefsKeys, selectedItemKey]);

    const scrollToSelectedItem = maxScrollPosition => {
        const targetRef = refs[selectedItemKey];
        if (config.scrollItemIntoView && targetRef?.current) {
            const pos = getScrollPosition(
                targetRef,
                targetRef.current?.offsetLeft,
                maxScrollPosition
            );
            setTargetScrollPosition(pos + config.scrollPositionOffset);
        }
    };

    const scrollToItemEnd = (sortedRefsOffsetLeft, target, lastItem, maxScrollPosition) => {
        if (lastItem) {
            return maxScrollPosition;
        }
        const containerGap = parseFloat(target?.current?.parentElement?.style.gap || 0);

        // filter only the refs that are after the selected one
        const filteredRefs = sortedRefsOffsetLeft.filter(
            ref => ref.current.offsetLeft > target?.current.offsetLeft
        );
        return (
            maxScrollPosition -
            filteredRefs.reduce((current, rightRef) => current + rightRef.current?.offsetWidth, 0) -
            filteredRefs.length * containerGap
        );
    };

    const getScrollPosition = (targetRef, offsetLeft, maxScrollPosition) => {
        const sortedRefsPosition = Object.values(refs).sort(
            (a, b) => a.current.offsetLeft - b.current.offsetLeft
        );

        const parentOffsetWidth = targetRef?.current.offsetParent?.offsetWidth;
        const parentPaddingLeft = parseFloat(
            targetRef?.current.parentElement?.style.paddingLeft || 0
        );
        const isLastItem = sortedRefsPosition.at(-1).current.offsetLeft === offsetLeft;
        const isFirstItem = sortedRefsPosition.at(0).current.offsetLeft === offsetLeft;

        switch (config.scrollItemIntoViewBehavior) {
            case SCROLL_BEHAVIOR.END:
                return scrollToItemEnd(
                    sortedRefsPosition,
                    targetRef,
                    isLastItem,
                    maxScrollPosition
                );
            case SCROLL_BEHAVIOR.START:
                return isFirstItem ? 0 : offsetLeft - parentPaddingLeft;
            default:
                return parentOffsetWidth
                    ? offsetLeft - parentOffsetWidth / 2 + targetRef?.current?.offsetWidth / 2
                    : 0;
        }
    };

    const onScrollPaneChanged = useCallback(scrollData => {
        setScrollPaneState({
            scrollPosition: scrollData.scrollPosition,
            maxScrollPosition: scrollData.maxScrollPosition,
            scrollPaneWidth: scrollData.viewportSize,
        });
    }, []);

    const onScrollPositionChanged = useCallback(scrollData => {
        setScrollPaneState({
            scrollPosition: scrollData.scrollPosition,
            maxScrollPosition: scrollData.maxScrollPosition,
            scrollPaneWidth: scrollData.viewportSize,
        });
    }, []);

    const onScrollPaneInit = useCallback((scrollData, el) => {
        onScrollPaneChanged(scrollData);
        scrollToSelectedItem(scrollData.maxScrollPosition);
        // Save the scroll element reference
        scrollRef.current = el;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onButtonScroll = (event, direction) => {
        const round = direction === SCROLL_DIRECTION.RIGHT ? Math.ceil : Math.floor;
        const delta = round(Math.abs(scrollPaneWidth * config.scrollFactor) * direction);
        setTargetScrollPosition(scrollPosition + delta);
        setIsAnimationEnabled(config.enableAnimation);
    };

    const className = classUtils.classNames('ta-ButtonScrollPane', componentClass);
    const areAllItemsVisible = useCallback(() => {
        if (!scrollPaneWidth || isEmpty(refs)) {
            return true;
        }

        const totalItemsWidth = Object.values(refs).reduce(
            (total, ref) => total + (ref.current?.offsetWidth ?? 0),
            0
        );
        return totalItemsWidth <= scrollPaneWidth;
    }, [scrollPaneWidth, refs]);

    const areScrollButtonsVisible =
        config.useButtonScrolling && (isHovered || showScrollButtons) && !areAllItemsVisible();

    return (
        <FlexPane
            class={className}
            elementRef={elementRef}
            config={style.scrollPaneContainer}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {areScrollButtonsVisible && (
                <ScrollButton
                    config={buildScrollButtonConfig(config, SCROLL_BUTTON_POSITION.LEFT)}
                    class={'ta-LeftScrollButton'}
                    onClick={onButtonScroll}
                    onClickData={SCROLL_DIRECTION.LEFT}
                    buttonRef={leftScrollButtonRef}
                    disabled={Math.floor(scrollPosition) === 0}
                />
            )}
            {!isEmpty(refs) && (
                <ScrollPane
                    targetPosition={targetScrollPosition}
                    onInit={onScrollPaneInit}
                    onScroll={onScrollPositionChanged}
                    onResize={onScrollPaneChanged}
                    enableAnimation={isAnimationEnabled}
                    config={style.scrollPane}
                >
                    <FlexPane config={config.contentContainer}>{children}</FlexPane>
                </ScrollPane>
            )}
            {areScrollButtonsVisible && (
                <ScrollButton
                    config={buildScrollButtonConfig(config, SCROLL_BUTTON_POSITION.RIGHT)}
                    class={'ta-RightScrollButton'}
                    onClick={onButtonScroll}
                    onClickData={SCROLL_DIRECTION.RIGHT}
                    buttonRef={rightScrollButtonRef}
                    disabled={Math.ceil(scrollPosition) === maxScrollPosition}
                />
            )}
        </FlexPane>
    );
};

const buildScrollButtonConfig = (config, direction) => {
    return {
        scrollButtonContainer: config[`${direction}ScrollButtonContainer`],
        scrollButton: config[`${direction}ScrollButton`],
        scrollButtonIcon: config[`${direction}ScrollButtonIcon`],
    };
};

const ScrollButton = props => {
    const { config, onClickData, onClick, class: className, buttonRef, disabled } = props;
    return (
        <AbsolutePane config={config.scrollButtonContainer}>
            <Button
                config={config.scrollButton}
                class={className}
                onClick={onClick}
                onClickData={onClickData}
                buttonElementRef={buttonRef}
                disabled={disabled}
            >
                <Image config={config.scrollButtonIcon} />
            </Button>
        </AbsolutePane>
    );
};

ButtonScrollPane.getStyle = (config, applicationMode, merge) => {
    const { scrollPaneContainer, useButtonScrolling } = config;
    const { nativeScroll, mouseDragScroll } = config.scrollPane;
    return {
        scrollPaneContainer: merge(scrollPaneContainer, { createStackingContext: true }),
        scrollPane: {
            ...config.scrollPane,
            direction: 'x',
            nativeScroll: nativeScroll || (applicationMode === 'mobile' && !useButtonScrolling),
            mouseDragScroll:
                mouseDragScroll || (applicationMode === 'desktop' && !useButtonScrolling),
        },
    };
};

export default ButtonScrollPane;
